import { create } from "zustand";
import {
  applyNodeChanges,
  applyEdgeChanges,
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
} from "@xyflow/react";
import { EDGE_MARKER_ID, EDGE_MARKER_START_ID } from "@/components/canvas/EdgeMarkers";
import {
  migrateLegacyCanvas,
  readCanvas,
  writeCanvas,
  lastActiveForgeId,
  canvasKey,
  type PersistedCanvas,
} from "./canvasPersistence";
import {
  wsService,
  type WsMessage,
  type NodeMovedPayload,
  type ShapeMovedPayload,
  type DeletedPayload,
} from "@/services/websocket";
import {
  mapNode,
  mapEdge,
  mapShape,
  type WireNode,
  type WireEdge,
  type WireShape,
} from "@/services/canvasWire";
import { fetchCanvas, type GetToken } from "@/services/canvasService";
import { useNodePageStore } from "./useNodePageStore";
import type { NodePage } from "@/services/nodePageService";
import { bbox, serializeDrawing, simplify, parseDrawing, type Point } from "@/utils/drawing";
import { CV } from "@/config/canvasSizing";
import { computeAutoLayout } from "@/components/canvas/autoLayout";

// ── Domain types (1-to-1 with Go internal/graph/models.go) ───────────────────

export interface CanvasNode {
  id: string;
  forgeId: string;
  type: string;
  label: string;
  icon?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  layer?: number;
  revealStep: number;
  drillForgeId?: string;
  color?: string;
  /** Page count from the canvas payload; badge source until pages are loaded. */
  pageCount?: number;
}

export type EdgeKind = "causes" | "part-of" | "leads-to" | "answered-by";

export interface CanvasEdge {
  id: string;
  forgeId: string;
  sourceId: string;
  targetId: string;
  labelText?: string;
  labelOffsetX: number;
  labelOffsetY: number;
  flowSpeed: number;
  flowLabel?: string;
  kind?: EdgeKind;
  revealStep: number;
  // When true, the flow dot ping-pongs back and forth instead of one-way,
  // and both ends render an arrowhead.
  bidirectional?: boolean;
}

export interface CanvasShape {
  id: string;
  forgeId: string;
  kind: string;
  content?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  revealStep: number;
  // Second endpoint for 'line'/'free_edge' kinds — two independently
  // draggable points instead of a resizable box. Undefined for every
  // other kind.
  x2?: number;
  y2?: number;
}

// Shapes drawn as a segment between two independently draggable endpoints
// (see LineShapeBody) rather than a resizable box.
export const LINE_SHAPE_KINDS = new Set([
  "line", "free_edge", "dashed_line", "double_arrow", "elbow",
]);

export interface ForgeCanvasPayload {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  shapes: CanvasShape[];
  /** The forge revision this canvas was read at — the base the next save states.
   *  Optional because a payload can come from somewhere with no opinion on it;
   *  applying one that omits it leaves the current base untouched rather than
   *  resetting it to 0, which would make the next save conflict on false
   *  grounds. Both real producers (fetchCanvas, cacheCurrentCanvas) always set it. */
  revision?: number;
}

// ── React Flow node/edge types ────────────────────────────────────────────────
// Intersection with Record<string,unknown> satisfies RF's generic constraint
// while preserving typed field access on .data.

export type KFNodeData = CanvasNode & Record<string, unknown>;
export type KFEdgeData = CanvasEdge & Record<string, unknown>;
export type KFNode = Node<KFNodeData>;
export type KFEdge = Edge<KFEdgeData>;

// ── Shape prefix — used to identify RF nodes that are shapes ─────────────────

export const SHAPE_PREFIX = "shape::";

// Wire → domain conversion lives in services/canvasWire.ts and is shared with the
// initial GET /canvas load. Do not add a second copy here: the one that used to
// live in this file drifted and silently dropped `bidirectional`.

// ── Domain → React Flow converters ────────────────────────────────────────────

function toRFNode(n: CanvasNode): KFNode {
  return {
    id: n.id,
    position: { x: n.x, y: n.y },
    data: n as KFNodeData,
    type: "kfNode",
    width: n.width,
    height: n.height,
  };
}

// Domain sourceId/targetId are always raw ids (what the backend validates
// against nodes+shapes). RF node ids for shapes carry SHAPE_PREFIX, so RF
// edge endpoints need it added back — otherwise ReactFlow can't find the
// shape's node and silently drops the edge.
function toRFId(id: string, shapeIds: Set<string>): string {
  return shapeIds.has(id) ? `${SHAPE_PREFIX}${id}` : id;
}

function stripShapePrefix(id: string): string {
  return id.startsWith(SHAPE_PREFIX) ? id.slice(SHAPE_PREFIX.length) : id;
}

function toRFEdge(e: CanvasEdge, shapeIds: Set<string>): KFEdge {
  return {
    id: e.id,
    source: toRFId(e.sourceId, shapeIds),
    target: toRFId(e.targetId, shapeIds),
    data: e as KFEdgeData,
    type: "kfEdge",
    label: e.labelText,
    markerEnd: EDGE_MARKER_ID,
    markerStart: e.bidirectional ? EDGE_MARKER_START_ID : undefined,
  };
}

export function toRFShape(s: CanvasShape): KFNode {
  if (LINE_SHAPE_KINDS.has(s.kind) && s.x2 != null && s.y2 != null) {
    const minX = Math.min(s.x, s.x2);
    const minY = Math.min(s.y, s.y2);
    return {
      id: `${SHAPE_PREFIX}${s.id}`,
      position: { x: minX, y: minY },
      data: s as unknown as KFNodeData,
      type: "kfShape",
      width: Math.max(1, Math.abs(s.x2 - s.x)),
      height: Math.max(1, Math.abs(s.y2 - s.y)),
      zIndex: s.zIndex,
    };
  }
  // Highlighter strokes lift above node cards so the translucent stroke reads
  // as a marker overlay rather than hiding behind cards drawn later.
  const isHighlighter = s.kind === "drawing" && !!parseDrawing(s.content)?.highlighter;
  return {
    id: `${SHAPE_PREFIX}${s.id}`,
    position: { x: s.x, y: s.y },
    data: s as unknown as KFNodeData,
    type: "kfShape",
    width: s.width,
    height: s.height,
    zIndex: isHighlighter ? CV.drawing.highlighterZIndex : s.zIndex,
  };
}

/** Relay a shape's geometry straight to peer browsers, the way moveNode does.
 *  Without this a shape drag only reached another device via the autosave tick
 *  plus a full refetch — visibly behind nodes, which land in one hop. */
function broadcastShapeGeometry(forgeId: string | null, s: CanvasShape): void {
  if (!forgeId) return;
  wsService.send<ShapeMovedPayload>({
    type: "shape_moved",
    forge_id: forgeId,
    payload: { id: s.id, x: s.x, y: s.y, x2: s.x2, y2: s.y2, width: s.width, height: s.height },
  });
}

// ── History ───────────────────────────────────────────────────────────────────

const MAX_HISTORY = 15;

interface HistoryEntry {
  rfNodes: KFNode[];
  rfEdges: KFEdge[];
  shapes: CanvasShape[];
  rfShapes: KFNode[];
}

// ── Store ─────────────────────────────────────────────────────────────────────

export type ActiveTool = "arrow" | "hand" | "pencil" | "eraser";

// ── Pencil settings persistence ─────────────────────────────────────────────────

const PENCIL_SETTINGS_KEY = "kf:pencilSettings";

interface PencilSettings {
  pencilColor: string;
  pencilWidth: number;
  pencilHighlighter: boolean;
}

const DEFAULT_PENCIL: PencilSettings = { pencilColor: "indigo", pencilWidth: 4, pencilHighlighter: false };

function loadPencilSettings(): PencilSettings {
  if (typeof window === "undefined") return { ...DEFAULT_PENCIL };
  try {
    const raw = localStorage.getItem(PENCIL_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_PENCIL };
    return { ...DEFAULT_PENCIL, ...(JSON.parse(raw) as Partial<PencilSettings>) };
  } catch {
    return { ...DEFAULT_PENCIL };
  }
}

function savePencilSettings(s: PencilSettings) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(PENCIL_SETTINGS_KEY, JSON.stringify(s)); } catch { /* ignore quota */ }
}

// Merge existing sequence with current canvas state, preserving drag-reordered positions.
// Keeps items that still have step > 0, appends newly-sequenced items at the end.
function syncRevealSequence(current: string[], rfShapes: KFNode[], rfNodes: KFNode[]): string[] {
  const allSequenced = [...rfShapes, ...rfNodes]
    .filter(n => (n.data as KFNodeData).revealStep > 0)
    .map(n => n.id);
  const sequencedSet = new Set(allSequenced);
  const kept = current.filter(id => sequencedSet.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...allSequenced.filter(id => !keptSet.has(id))];
}

// Module-level cache — outside Zustand state to avoid re-render overhead.
// Maps forgeId → last-known payload for instant restore on switch-back.
const _canvasCache = new Map<string, ForgeCanvasPayload>();
// Forges just created: empty canvas, skip server fetch.
const _freshForges = new Set<string>();
const CACHE_LIMIT = 5;

/** Clear module-level internals — for tests only. */
export function _resetCanvasInternals() {
  _canvasCache.clear();
  _freshForges.clear();
}

interface CanvasStore {
  forgeId: string | null;
  rfNodes: KFNode[];
  rfEdges: KFEdge[];
  shapes: CanvasShape[];
  rfShapes: KFNode[];
  activeTool: ActiveTool;
  pencilColor: string;
  pencilWidth: number;
  pencilHighlighter: boolean;
  // Whether the pencil options panel is shown. Opens when the pencil tool is
  // (re)selected; the first stroke closes it so it stays out of the way.
  pencilOptionsOpen: boolean;
  selectedIds: Set<string>;
  past: HistoryEntry[];
  future: HistoryEntry[];
  isLoading: boolean;
  error: string | null;

  // Reveal playback
  isRevealMode: boolean;
  revealPointer: number;

  // Reveal edit
  isRevealEditMode: boolean;
  revealHighlightId: string | null;
  revealSequence: string[];  // ordered rfIds (step > 0), drives stagger animation

  // Hand-tool click-to-focus: the node the pan tool last clicked. Drives the
  // zoom-to-centre camera and the yellow "selected" highlight. focusTick bumps on
  // every click so re-clicking the same node re-fires the camera.
  focusNodeId: string | null;
  focusTick: number;

  // Save tracking. `revision` bumps on every local edit; `syncedRevision` is the
  // revision the server last confirmed. Unsaved work ⟺ the two differ. A counter
  // rather than timestamps: `forges` has no `updated_at`, and "which side is
  // newer" is the wrong question anyway once MCP can write server-side.
  revision: number;
  syncedRevision: number;
  // The *server's* counter for this forge, as of the last load or accepted
  // save. Travels with each save as `base_revision`; a save built on a stale
  // one is refused with 409 rather than applied on top of someone else's write.
  //
  // Deliberately not the same number as `revision` above: that one counts local
  // edits and is meaningless off this device. Two devices editing one forge
  // have unrelated `revision` values and the same `serverRevision`.
  serverRevision: number;
  consecutiveSaveFailures: number;
  saveError: string | null;

  // Every id this client has evidence the server holds (or held): loaded with the
  // canvas, created here, or received as a live patch. It travels with each save
  // as `known_ids` and is the only scope the server will delete within.
  //
  // A save used to be a full replace, so anything missing from the payload was
  // pruned — which meant a tab that had not heard about an MCP write erased it,
  // silently, along with the node's pages. The client now states what it has
  // authority over instead of the server inferring it from absence. Ids stay in
  // here after the element is deleted locally: "I knew about this and did not
  // send it" is exactly how a delete is communicated.
  knownIds: Set<string>;

  // RF ids of elements added over the WS that React Flow may not have measured.
  // A hidden/backgrounded tab delivers no ResizeObserver callbacks, so such a
  // node keeps `measured: {}` and RF silently skips every edge touching it.
  // MeasureHealer (inside <ReactFlow>) drains this queue.
  pendingMeasureIds: string[];

  onNodesChange: OnNodesChange<KFNode>;
  onEdgesChange: OnEdgesChange<KFEdge>;
  setActiveTool: (tool: ActiveTool) => void;
  setPencilColor: (color: string) => void;
  setPencilWidth: (width: number) => void;
  setPencilHighlighter: (on: boolean) => void;
  setPencilOptionsOpen: (open: boolean) => void;
  setSelectedIds: (ids: Set<string>) => void;
  setSelection: (ids: Set<string>) => void;
  focusNode: (id: string) => void;
  clearFocus: () => void;

  snapshot: () => void;
  undo: () => void;
  redo: () => void;

  applyServerCanvas: (payload: ForgeCanvasPayload) => void;
  /** Merge the server's canvas into a local one that has unsaved edits, after a
   *  save was refused as stale. Not a reload: local work exists nowhere else. */
  reconcileServerCanvas: (payload: ForgeCanvasPayload) => void;
  loadCanvas: (forgeId: string, getToken: GetToken) => Promise<void>;
  seedFreshForge: (forgeId: string) => void;
  cacheCurrentCanvas: () => void;
  addNode: (type: string, pos?: { x: number; y: number }, icon?: string, label?: string) => void;
  addEdge: (source: string, target: string) => void;
  addShape: (kind: string, pos?: { x: number; y: number }) => void;
  addDrawing: (points: Point[], style: { color: string; width: number; highlighter?: boolean }) => void;
  updateNode: (id: string, patch: Partial<CanvasNode>) => void;
  updateEdge: (id: string, patch: Partial<CanvasEdge>) => void;
  updateShape: (id: string, patch: Partial<CanvasShape>) => void;
  bulkUpdateRevealStep: (rfIds: string[], step: number) => void;
  moveNode: (id: string, x: number, y: number) => void;
  rebuildLayout: () => void;
  resizeNode: (id: string, x: number, y: number, width: number, height: number) => void;
  moveShape: (id: string, x: number, y: number) => void;
  resizeShape: (id: string, width: number, height: number) => void;
  updateShapeEndpoint: (id: string, which: "a" | "b", x: number, y: number) => void;
  deleteElements: (ids: { nodeIds?: string[]; edgeIds?: string[]; shapeIds?: string[] }) => void;
  reconnectEdge: (id: string, source: string, target: string) => void;
  detachEdgeToShape: (edgeId: string, ax: number, ay: number, bx: number, by: number) => void;
  applyWsMessage: (msg: WsMessage) => void;
  clearPendingMeasureIds: (ids: string[]) => void;
  /** Confirm the revision the server accepted. Pass the revision captured
   *  *before* the request, so edits made in flight stay unsaved; the ids that
   *  request sent, which become the new delete scope; and the forge revision
   *  the server reported back, which is the base for the next save. */
  markSaved: (revision: number, sentIds: string[], serverRevision: number) => void;
  recordSaveFailure: (reason: string) => void;

  // Reveal actions
  enterRevealMode: () => void;
  exitRevealMode: () => void;
  advanceReveal: () => void;
  retreatReveal: () => void;
  enterRevealEditMode: () => void;
  exitRevealEditMode: () => void;
  setRevealHighlightId: (id: string | null) => void;
  removeFromRevealSequence: (rfId: string) => void;
  moveToRevealStep: (rfId: string, step: number) => void;
  reorderWithinStep: (rfId: string, beforeRfId: string | null, step: number) => void;
}

export const useCanvasStore = create<CanvasStore>((set, get) => ({
  forgeId: null,
  rfNodes: [],
  rfEdges: [],
  shapes: [],
  rfShapes: [],
  activeTool: "arrow",
  ...loadPencilSettings(),
  pencilOptionsOpen: true,
  selectedIds: new Set<string>(),
  past: [],
  future: [],
  pendingMeasureIds: [],
  revision: 0,
  syncedRevision: 0,
  serverRevision: 0,
  consecutiveSaveFailures: 0,
  saveError: null,
  knownIds: new Set<string>(),
  isLoading: false,
  error: null,
  isRevealMode: false,
  revealPointer: 0,
  isRevealEditMode: false,
  revealHighlightId: null,
  revealSequence: [],
  focusNodeId: null,
  focusTick: 0,

  // Selecting (or re-tapping) the pencil reopens its options; any other tool
  // leaves the flag irrelevant since the panel only mounts for the pencil.
  setActiveTool: (tool) => set({
    activeTool: tool,
    pencilOptionsOpen: tool === "pencil",
    // Focus is a hand-tool concept — drop the highlight when the tool changes.
    focusNodeId: null,
    ...(tool === "hand" ? { selectedIds: new Set<string>() } : {}),
  }),
  setPencilColor: (color) => {
    set({ pencilColor: color });
    const { pencilWidth, pencilHighlighter } = get();
    savePencilSettings({ pencilColor: color, pencilWidth, pencilHighlighter });
  },
  setPencilWidth: (width) => {
    set({ pencilWidth: width });
    const { pencilColor, pencilHighlighter } = get();
    savePencilSettings({ pencilColor, pencilWidth: width, pencilHighlighter });
  },
  setPencilHighlighter: (on) => {
    set({ pencilHighlighter: on });
    const { pencilColor, pencilWidth } = get();
    savePencilSettings({ pencilColor, pencilWidth, pencilHighlighter: on });
  },
  setPencilOptionsOpen: (open) => {
    if (get().pencilOptionsOpen === open) return;
    set({ pencilOptionsOpen: open });
  },
  setSelectedIds: (ids) => {
    const current = get().selectedIds;
    if (current.size === ids.size && [...ids].every(id => current.has(id))) return;
    set({ selectedIds: ids });
  },
  focusNode: (id) => set((s) => ({ focusNodeId: id, focusTick: s.focusTick + 1 })),
  clearFocus: () => { if (get().focusNodeId !== null) set({ focusNodeId: null }); },

  // Controlled selection for mobile additive tap: sets both `selectedIds` and the
  // `selected` flag on every node/shape/edge so RF styling, resizer, and multi-drag
  // stay in sync. Objects are only cloned when their selected state actually changes.
  setSelection: (ids) => {
    const { rfNodes, rfShapes, rfEdges } = get();
    set({
      selectedIds: ids,
      rfNodes:  rfNodes.map(n  => (!!n.selected === ids.has(n.id) ? n : { ...n, selected: ids.has(n.id) })),
      rfShapes: rfShapes.map(n => (!!n.selected === ids.has(n.id) ? n : { ...n, selected: ids.has(n.id) })),
      rfEdges:  rfEdges.map(e  => (!!e.selected === ids.has(e.id) ? e : { ...e, selected: ids.has(e.id) })),
    });
  },

  // Route changes by ID prefix — shapes use "shape::" prefix.
  // Remove changes route through deleteElements (cascade + snapshot).
  onNodesChange: (changes) => {
    type WithId = { id?: string };
    const removeIds = changes
      .filter(c => c.type === "remove")
      .map(c => (c as unknown as WithId).id)
      .filter((id): id is string => !!id);

    if (removeIds.length > 0) {
      get().deleteElements({
        nodeIds:  removeIds.filter(id => !id.startsWith(SHAPE_PREFIX)),
        shapeIds: removeIds.filter(id => id.startsWith(SHAPE_PREFIX)).map(id => id.slice(SHAPE_PREFIX.length)),
      });
    }

    const nonRemove = changes.filter(c => c.type !== "remove");
    if (nonRemove.length === 0) return;
    const { rfNodes, rfShapes } = get();
    const nodeChanges  = nonRemove.filter(c => !(c as unknown as WithId).id?.startsWith(SHAPE_PREFIX));
    const shapeChanges = nonRemove.filter(c => !!(c as unknown as WithId).id?.startsWith(SHAPE_PREFIX));
    set({
      rfNodes:  applyNodeChanges(nodeChanges,  rfNodes),
      rfShapes: applyNodeChanges(shapeChanges, rfShapes),
    });
  },

  onEdgesChange: (changes) => {
    const removeIds = changes
      .filter(c => c.type === "remove")
      .map(c => (c as unknown as { id?: string }).id)
      .filter((id): id is string => !!id);

    if (removeIds.length > 0) get().deleteElements({ edgeIds: removeIds });

    const nonRemove = changes.filter(c => c.type !== "remove");
    if (nonRemove.length > 0) set({ rfEdges: applyEdgeChanges(nonRemove, get().rfEdges) });
  },

  // Every user mutation calls this, so it is also where "the canvas now differs
  // from what the server has" is recorded. applyServerCanvas calls it too, but
  // ends by re-syncing the counters — a fresh load is in sync by definition.
  snapshot: () => {
    const { rfNodes, rfEdges, shapes, rfShapes, past, revision } = get();
    set({
      past: [...past.slice(-(MAX_HISTORY - 1)), { rfNodes, rfEdges, shapes, rfShapes }],
      future: [],
      revision: revision + 1,
    });
  },

  // undo/redo don't go through snapshot(), but they change the canvas relative
  // to the server exactly as an edit does — so they bump revision themselves.
  undo: () => {
    const { past, future, rfNodes, rfEdges, shapes, rfShapes, revision } = get();
    if (past.length === 0) return;
    const prev = past[past.length - 1];
    set({
      past: past.slice(0, -1),
      future: [{ rfNodes, rfEdges, shapes, rfShapes }, ...future],
      rfNodes: prev.rfNodes,
      rfEdges: prev.rfEdges,
      shapes: prev.shapes,
      rfShapes: prev.rfShapes,
      revision: revision + 1,
    });
  },

  redo: () => {
    const { past, future, rfNodes, rfEdges, shapes, rfShapes, revision } = get();
    if (future.length === 0) return;
    const next = future[0];
    set({
      past: [...past, { rfNodes, rfEdges, shapes, rfShapes }],
      future: future.slice(1),
      rfNodes: next.rfNodes,
      rfEdges: next.rfEdges,
      shapes: next.shapes,
      rfShapes: next.rfShapes,
      revision: revision + 1,
    });
  },

  applyServerCanvas: ({ nodes, edges, shapes, revision }) => {
    get().snapshot();
    const rfNodes  = nodes.map(toRFNode);
    const rfShapes = shapes.map(toRFShape);
    const shapeIds = new Set(shapes.map((s) => s.id));
    set({
      rfNodes,
      rfEdges: edges.map((e) => toRFEdge(e, shapeIds)),
      shapes,
      rfShapes,
      revealSequence: syncRevealSequence(get().revealSequence, rfShapes, rfNodes),
      // This canvas *is* what the server holds, so it is in sync by definition —
      // including the bump from the snapshot() above.
      syncedRevision: get().revision,
      // A payload with no opinion on the revision leaves the base alone; see
      // ForgeCanvasPayload.revision.
      serverRevision: revision ?? get().serverRevision,
      // Replaced, not merged: these are the ids of the canvas now on screen, and
      // carrying another forge's ids across would claim delete authority over
      // rows this canvas has nothing to do with.
      knownIds: new Set([
        ...nodes.map((n) => n.id),
        ...edges.map((e) => e.id),
        ...shapes.map((s) => s.id),
      ]),
      consecutiveSaveFailures: 0,
      saveError: null,
      // A canvas that mounts while the tab is hidden gets no ResizeObserver
      // callbacks either, leaving every node unmeasured and every edge skipped.
      // Queue the whole canvas so MeasureHealer measures it up front.
      pendingMeasureIds: [...rfNodes.map((n) => n.id), ...rfShapes.map((s) => s.id)],
    });
  },

  // Recovery from a 409: the server refused this client's save as stale. It is
  // behind, but its unsaved edits exist nowhere else, so this cannot be a plain
  // reload — that would silently destroy them, which is the failure mode the
  // whole revision mechanism was added to prevent.
  //
  // The merge rule, in one line: the server decides what EXISTS, the local copy
  // decides what those rows SAY.
  //
  // Existence turns on knownIds, and this is the subtle half. A local row the
  // server did not return is absent for one of two opposite reasons — another
  // device deleted it, or this device just created it and no save has landed
  // yet. knownIds already records exactly that difference (it is the client's
  // statement of what the server has acknowledged), so it decides: known and
  // absent means deleted elsewhere, unknown and absent means locally new.
  //
  // With no unsaved edits there is nothing to protect and nothing to merge, so
  // the server payload is taken wholesale.
  reconcileServerCanvas: (payload) => {
    const state = get();
    if (state.revision === state.syncedRevision) {
      get().applyServerCanvas(payload);
      return;
    }

    const { nodes, edges, shapes, revision } = payload;
    const known = state.knownIds;
    const serverNodes = new Map(nodes.map((n) => [n.id, n]));
    const serverShapes = new Map(shapes.map((s) => [s.id, s]));
    const serverEdges = new Map(edges.map((e) => [e.id, e]));

    // Survives if the server still has it, or if the server has never heard of
    // it. Dies only when the server knew it and dropped it.
    const survives = (id: string, onServer: boolean) => onServer || !known.has(id);

    const keptNodes = state.rfNodes.filter((n) => survives(n.id, serverNodes.has(n.id)));
    const keptShapes = state.shapes.filter((s) => survives(s.id, serverShapes.has(s.id)));

    // Rows this client has never seen — created on another device.
    const localNodeIds = new Set(keptNodes.map((n) => n.id));
    const localShapeIds = new Set(keptShapes.map((s) => s.id));
    const addedNodes = nodes.filter((n) => !localNodeIds.has(n.id));
    const addedShapes = shapes.filter((s) => !localShapeIds.has(s.id));

    const mergedNodes = [...keptNodes, ...addedNodes.map(toRFNode)];
    const mergedShapes = [...keptShapes, ...addedShapes];

    // An edge whose endpoint just disappeared has nothing to attach to, and
    // React Flow drops such edges silently rather than reporting them. The
    // server sweeps the same orphans in SQL, so this keeps the two in step.
    const endpointIds = new Set<string>([
      ...mergedNodes.map((n) => n.id),
      ...mergedShapes.map((s) => s.id),
    ]);
    const attached = (e: CanvasEdge | undefined) =>
      !!e && endpointIds.has(e.sourceId) && endpointIds.has(e.targetId);

    const keptEdges = state.rfEdges.filter(
      (e) => survives(e.id, serverEdges.has(e.id)) && attached(e.data),
    );
    const localEdgeIds = new Set(keptEdges.map((e) => e.id));
    const addedEdges = edges.filter((e) => !localEdgeIds.has(e.id) && attached(e));

    const shapeIds = new Set(mergedShapes.map((s) => s.id));
    const mergedRfShapes = mergedShapes.map(toRFShape);

    set({
      rfNodes: mergedNodes,
      rfEdges: [...keptEdges, ...addedEdges.map((e) => toRFEdge(e, shapeIds))],
      shapes: mergedShapes,
      rfShapes: mergedRfShapes,
      revealSequence: syncRevealSequence(state.revealSequence, mergedRfShapes, mergedNodes),
      // The base the retry will state. Without this the retry conflicts again on
      // identical grounds and the client never converges.
      serverRevision: revision ?? state.serverRevision,
      // revision/syncedRevision are deliberately untouched: the local edits are
      // still unsaved, and the next autosave tick retries them against the new
      // base.
      //
      // Authority follows the server's report, not the merge: rows kept only
      // because they are locally new are NOT claimed here — they enter the scope
      // when a save of them succeeds, exactly as a freshly created node does.
      knownIds: new Set([
        ...nodes.map((n) => n.id),
        ...edges.map((e) => e.id),
        ...shapes.map((s) => s.id),
      ]),
      // Whatever arrived from the server has never been through this tab's
      // layout pass — same reasoning as applyServerCanvas.
      pendingMeasureIds: [
        ...addedNodes.map((n) => n.id),
        ...addedShapes.map((s) => toRFShape(s).id),
      ],
    });
  },

  seedFreshForge: (forgeId) => {
    _freshForges.add(forgeId);
    set({
      forgeId,
      rfNodes: [],
      rfEdges: [],
      shapes: [],
      rfShapes: [],
      past: [],
      future: [],
      isLoading: false,
      error: null,
      revealSequence: [],
    });
  },

  cacheCurrentCanvas: () => {
    const { forgeId, rfNodes, rfEdges, shapes, serverRevision } = get();
    if (!forgeId) return;
    _canvasCache.set(forgeId, {
      nodes: rfNodes.map((n) => n.data),
      edges: rfEdges.map((e) => e.data).filter((d): d is NonNullable<typeof d> => d != null),
      shapes,
      // Carried so returning to a cached forge restores its base too. Without
      // it the first save after a forge switch states whatever base the *other*
      // forge was on, and is refused.
      revision: serverRevision,
    });
    if (_canvasCache.size > CACHE_LIMIT) {
      const oldest = _canvasCache.keys().next().value;
      if (oldest !== undefined) _canvasCache.delete(oldest);
    }
  },

  loadCanvas: async (forgeId, getToken) => {
    // Fresh forge — pre-seeded as empty, skip server round-trip.
    if (_freshForges.has(forgeId)) {
      _freshForges.delete(forgeId);
      return;
    }
    // The in-memory canvas was rehydrated from localStorage and holds edits the
    // server never got. Applying the server payload here would stamp its older
    // copy straight over them — the actual mechanism by which unsaved work was
    // silently lost. Keep the local canvas; autosave pushes it up.
    const local = get();
    if (local.forgeId === forgeId && local.revision !== local.syncedRevision) {
      set({ isLoading: false, error: null });
      return;
    }
    // A stored canvas outranks the server only when it holds edits the server
    // never received — that work exists nowhere else. A fully-synced local copy
    // must NOT win: writes can land server-side while the tab is closed (MCP
    // does exactly that), and serving the local copy would hide them.
    const stored = readCanvas(forgeId);
    if (stored && stored.revision !== stored.syncedRevision) {
      set({ isLoading: false, error: null, ...persistedState(stored) });
      return;
    }
    // Cache hit — restore without a network call.
    const cached = _canvasCache.get(forgeId);
    if (cached) {
      set({ isLoading: false, error: null, forgeId });
      get().applyServerCanvas(cached);
      return;
    }
    // Normal fetch.
    set({ isLoading: true, error: null, forgeId });
    try {
      const payload = await fetchCanvas(forgeId, getToken);
      _canvasCache.set(forgeId, payload);
      if (_canvasCache.size > CACHE_LIMIT) {
        const oldest = _canvasCache.keys().next().value;
        if (oldest !== undefined) _canvasCache.delete(oldest);
      }
      get().applyServerCanvas(payload);
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Failed to load canvas" });
    } finally {
      set({ isLoading: false });
    }
  },

  addNode: (type, pos, icon, label) => {
    const { rfNodes, forgeId } = get();
    if (!forgeId) return;
    const offset = (rfNodes.length % 6) * 30;
    const node: CanvasNode = {
      id: crypto.randomUUID(),
      forgeId,
      type,
      label: label ?? type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, " "),
      icon,
      x: pos ? pos.x - CV.node.defaultW / 2 : 200 + offset,
      y: pos ? pos.y - CV.node.defaultH / 2 : 200 + offset,
      width: CV.node.defaultW,
      height: CV.node.defaultH,
      revealStep: 0,
    };
    get().snapshot();
    set({ rfNodes: [...rfNodes, toRFNode(node)] });
  },

  addEdge: (source, target) => {
    const { rfEdges, forgeId } = get();
    if (!forgeId) return;
    const edge: CanvasEdge = {
      id: crypto.randomUUID(),
      forgeId,
      sourceId: stripShapePrefix(source),
      targetId: stripShapePrefix(target),
      labelOffsetX: 0,
      labelOffsetY: 0,
      flowSpeed: 0,
      revealStep: 0,
    };
    get().snapshot();
    // Use the caller's RF ids (source/target) directly for RF's source/target —
    // they're already correct (RF just told us which node it connected to).
    // Re-deriving via shapeIds here would misfire on a stale/empty shapes list.
    set({
      rfEdges: [...rfEdges, {
        id: edge.id,
        source,
        target,
        data: edge as KFEdgeData,
        type: "kfEdge",
        label: edge.labelText,
        markerEnd: EDGE_MARKER_ID,
      }],
    });
  },

  addShape: (kind, pos) => {
    const { shapes, rfShapes, forgeId } = get();
    if (!forgeId) return;
    const offset = (shapes.length % 6) * 30;

    let shape: CanvasShape;
    if (LINE_SHAPE_KINDS.has(kind)) {
      const { dx, dy } = CV.shape.lineDefault;
      const x = pos ? pos.x : 200 + offset;
      const y = pos ? pos.y : 200 + offset;
      shape = {
        id: crypto.randomUUID(),
        forgeId,
        kind,
        x, y,
        x2: x + dx,
        y2: y + dy,
        width: Math.max(1, Math.abs(dx)),
        height: Math.max(1, Math.abs(dy)),
        zIndex: 0,
        revealStep: 0,
      };
    } else {
      const sd = CV.shape.defaults;
      const dims = sd[kind as keyof typeof sd] ?? sd.fallback;
      shape = {
        id: crypto.randomUUID(),
        forgeId,
        kind,
        content: "",
        x: pos ? pos.x - dims.width / 2  : 200 + offset,
        y: pos ? pos.y - dims.height / 2 : 200 + offset,
        ...dims,
        zIndex: 0,
        revealStep: 0,
      };
    }
    get().snapshot();
    // Select the new shape (and deselect everything else) so it's immediately
    // obvious where it landed — otherwise a pale shape on a pale canvas is easy
    // to miss, especially on mobile.
    const rfId = `${SHAPE_PREFIX}${shape.id}`;
    const newRfShape = { ...toRFShape(shape), selected: true };
    set({
      shapes: [...shapes, shape],
      rfShapes: [...rfShapes.map(s => (s.selected ? { ...s, selected: false } : s)), newRfShape],
      rfNodes: get().rfNodes.map(n => (n.selected ? { ...n, selected: false } : n)),
      selectedIds: new Set([rfId]),
    });
  },

  // Freehand stroke → CanvasShape of kind "drawing". Absolute flow-coord points
  // are stored relative to the bbox origin in `content` so moving the shape never
  // needs to rewrite the point list (see utils/drawing.ts).
  addDrawing: (points, style) => {
    const { shapes, rfShapes, forgeId } = get();
    if (!forgeId || points.length < 2) return;
    // Drop redundant points (RDP) so long strokes stay compact in storage and
    // cheap to re-render; the visible line is unchanged.
    const simplified = simplify(points, CV.drawing.simplifyEpsilon);
    const box = bbox(simplified);
    const relative = simplified.map((p): Point => [p[0] - box.x, p[1] - box.y]);
    const shape: CanvasShape = {
      id: crypto.randomUUID(),
      forgeId,
      kind: "drawing",
      content: serializeDrawing({
        points: relative,
        color: style.color,
        width: style.width,
        highlighter: style.highlighter ?? false,
      }),
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      zIndex: 0,
      revealStep: 0,
    };
    get().snapshot();
    set({ shapes: [...shapes, shape], rfShapes: [...rfShapes, toRFShape(shape)] });
  },

  updateNode: (id, patch) => {
    get().snapshot();
    set({
      rfNodes: get().rfNodes.map(n =>
        n.id === id ? { ...n, data: { ...n.data, ...patch } } : n,
      ),
    });
  },

  updateEdge: (id, patch) => {
    get().snapshot();
    set({
      rfEdges: get().rfEdges.map(e => {
        if (e.id !== id) return e;
        const data = { ...e.data, ...patch } as KFEdgeData;
        return {
          ...e,
          data,
          markerStart: data.bidirectional ? EDGE_MARKER_START_ID : undefined,
        };
      }),
    });
  },

  updateShape: (id, patch) => {
    const { shapes, rfShapes } = get();
    get().snapshot();
    set({
      shapes: shapes.map(s => s.id === id ? { ...s, ...patch } : s),
      rfShapes: rfShapes.map(n =>
        n.id === `${SHAPE_PREFIX}${id}` ? { ...n, data: { ...n.data, ...patch } } : n,
      ),
    });
  },

  bulkUpdateRevealStep: (rfIds, step) => {
    const idSet = new Set(rfIds);
    const { rfNodes, rfEdges, rfShapes, shapes } = get();
    get().snapshot();
    set({
      rfNodes:  rfNodes.map(n  => idSet.has(n.id)  ? { ...n,  data: { ...n.data,  revealStep: step } } : n),
      rfEdges:  rfEdges.map(e  => idSet.has(e.id)  ? { ...e,  data: { ...e.data,  revealStep: step } as KFEdgeData } : e),
      rfShapes: rfShapes.map(n => idSet.has(n.id)  ? { ...n,  data: { ...n.data,  revealStep: step } } : n),
      shapes:   shapes.map(s   => idSet.has(`${SHAPE_PREFIX}${s.id}`) ? { ...s, revealStep: step } : s),
    });
  },

  resizeNode: (id, x, y, width, height) => {
    const { rfNodes } = get();
    get().snapshot();
    set({
      rfNodes: rfNodes.map((n) =>
        n.id === id
          ? { ...n, position: { x, y }, width, height, data: { ...n.data, x, y, width, height } }
          : n,
      ),
    });
  },

  moveNode: (id, x, y) => {
    const { rfNodes, forgeId } = get();
    get().snapshot();
    set({
      rfNodes: rfNodes.map((n) =>
        n.id === id
          ? { ...n, position: { x, y }, data: { ...n.data, x, y } }
          : n,
      ),
    });
    if (forgeId) {
      wsService.send<NodeMovedPayload>({
        type: "node_moved",
        forge_id: forgeId,
        payload: { id, x, y },
      });
    }
  },

  // "Rebuild map" — re-run Dagre auto-layout over the real nodes (shapes keep
  // their positions) and broadcast each new position so it persists like a
  // manual drag. One snapshot for a single undo of the whole re-layout.
  rebuildLayout: () => {
    const { rfNodes, rfEdges, forgeId } = get();
    if (rfNodes.length === 0) return;
    const positions = computeAutoLayout(
      rfNodes.map((n) => ({
        id: n.id,
        width: n.width ?? CV.node.defaultW,
        height: n.height ?? CV.node.defaultH,
      })),
      rfEdges.map((e) => ({ source: e.source, target: e.target })),
    );
    get().snapshot();
    set({
      rfNodes: rfNodes.map((n) => {
        const p = positions[n.id];
        return p ? { ...n, position: { x: p.x, y: p.y }, data: { ...n.data, x: p.x, y: p.y } } : n;
      }),
    });
    if (forgeId) {
      Object.entries(positions).forEach(([id, p]) => {
        wsService.send<NodeMovedPayload>({
          type: "node_moved",
          forge_id: forgeId,
          payload: { id, x: p.x, y: p.y },
        });
      });
    }
  },

  moveShape: (id, x, y) => {
    const { rfShapes, shapes } = get();
    const current = shapes.find((s) => s.id === id);

    // Line-like shapes: (x, y) here is the RF bounding-box origin, not endpoint
    // A directly — translate both endpoints by the same delta so the line
    // keeps its shape/angle instead of endpoint A jumping to the box corner.
    if (current && LINE_SHAPE_KINDS.has(current.kind) && current.x2 != null && current.y2 != null) {
      const minX = Math.min(current.x, current.x2);
      const minY = Math.min(current.y, current.y2);
      const dx = x - minX;
      const dy = y - minY;
      const updated: CanvasShape = {
        ...current,
        x: current.x + dx,
        y: current.y + dy,
        x2: current.x2 + dx,
        y2: current.y2 + dy,
      };
      set({
        rfShapes: rfShapes.map((n) => (n.id === `${SHAPE_PREFIX}${id}` ? toRFShape(updated) : n)),
        shapes: shapes.map((s) => (s.id === id ? updated : s)),
      });
      broadcastShapeGeometry(get().forgeId, updated);
      return;
    }

    const moved = current ? { ...current, x, y } : undefined;
    set({
      rfShapes: rfShapes.map((n) =>
        n.id === `${SHAPE_PREFIX}${id}`
          ? { ...n, position: { x, y }, data: { ...n.data, x, y } }
          : n,
      ),
      shapes: shapes.map((s) => (s.id === id ? { ...s, x, y } : s)),
    });
    if (moved) broadcastShapeGeometry(get().forgeId, moved);
  },

  resizeShape: (id, width, height) => {
    const { rfShapes, shapes } = get();
    const current = shapes.find((s) => s.id === id);
    set({
      rfShapes: rfShapes.map((n) =>
        n.id === `${SHAPE_PREFIX}${id}`
          ? { ...n, width, height, data: { ...n.data, width, height } }
          : n,
      ),
      shapes: shapes.map((s) => (s.id === id ? { ...s, width, height } : s)),
    });
    if (current) broadcastShapeGeometry(get().forgeId, { ...current, width, height });
  },

  updateShapeEndpoint: (id, which, x, y) => {
    const { rfShapes, shapes } = get();
    const current = shapes.find((s) => s.id === id);
    if (!current) return;
    const updated: CanvasShape = which === "a"
      ? { ...current, x, y }
      : { ...current, x2: x, y2: y };
    set({
      rfShapes: rfShapes.map((n) => (n.id === `${SHAPE_PREFIX}${id}` ? toRFShape(updated) : n)),
      shapes: shapes.map((s) => (s.id === id ? updated : s)),
    });
    broadcastShapeGeometry(get().forgeId, updated);
  },

  deleteElements: ({ nodeIds = [], edgeIds = [], shapeIds = [] }) => {
    if (!nodeIds.length && !edgeIds.length && !shapeIds.length) return;
    const { rfNodes, rfEdges, rfShapes, shapes } = get();
    get().snapshot();

    const deleteNodeSet  = new Set(nodeIds);
    const deleteEdgeSet  = new Set(edgeIds);
    const deleteShapeSet = new Set(shapeIds);

    const newRfNodes = rfNodes.filter((n) => !deleteNodeSet.has(n.id));
    const survivingNodeIds = new Set(newRfNodes.map((n) => n.id));

    // Also cascade-delete edges whose source or target no longer exists
    const newRfEdges = rfEdges.filter(
      (e) =>
        !deleteEdgeSet.has(e.id) &&
        survivingNodeIds.has(e.source) &&
        survivingNodeIds.has(e.target),
    );

    set({
      rfNodes:  newRfNodes,
      rfEdges:  newRfEdges,
      rfShapes: rfShapes.filter((n) => !deleteShapeSet.has(n.id.slice(SHAPE_PREFIX.length))),
      shapes:   shapes.filter((s) => !deleteShapeSet.has(s.id)),
    });
  },

  reconnectEdge: (id, source, target) => {
    get().snapshot();
    set({
      rfEdges: get().rfEdges.map(e =>
        e.id === id
          ? {
              ...e,
              source,
              target,
              data: {
                ...e.data,
                sourceId: stripShapePrefix(source),
                targetId: stripShapePrefix(target),
              } as KFEdgeData,
            }
          : e,
      ),
    });
  },

  // Drag a connected edge's endpoint onto empty canvas → turn the edge into a
  // free-floating arrow shape spanning (ax,ay)→(bx,by) in flow coords. The whole
  // canvas is autosaved wholesale, so removing the edge + adding the shape here
  // is enough — no per-op REST call.
  detachEdgeToShape: (edgeId, ax, ay, bx, by) => {
    const { rfEdges, shapes, rfShapes, rfNodes, forgeId } = get();
    const edge = rfEdges.find((e) => e.id === edgeId);
    if (!edge) return;
    get().snapshot();

    const kind = edge.data?.bidirectional ? "double_arrow" : "free_edge";
    const shape: CanvasShape = {
      id: crypto.randomUUID(),
      forgeId: edge.data?.forgeId ?? forgeId ?? "",
      kind,
      x: ax,
      y: ay,
      x2: bx,
      y2: by,
      width: Math.max(1, Math.abs(bx - ax)),
      height: Math.max(1, Math.abs(by - ay)),
      zIndex: 0,
      revealStep: edge.data?.revealStep ?? 0,
    };
    const rfId = `${SHAPE_PREFIX}${shape.id}`;
    set({
      rfEdges: rfEdges.filter((e) => e.id !== edgeId),
      shapes: [...shapes, shape],
      rfShapes: [...rfShapes.map((s) => (s.selected ? { ...s, selected: false } : s)), { ...toRFShape(shape), selected: true }],
      rfNodes: rfNodes.map((n) => (n.selected ? { ...n, selected: false } : n)),
      selectedIds: new Set([rfId]),
    });
  },

  // Remote patches (peer browsers and MCP tool writes) are applied without taking
  // an undo snapshot — work the local user did not do is not a step in their undo
  // stack. Every case is idempotent: the hub can redeliver, and a reconnect can
  // overlap a fresh load.
  applyWsMessage: (msg) => {
    const { rfNodes, rfEdges } = get();

    switch (msg.type) {
      case "node_moved": {
        const p = msg.payload as NodeMovedPayload;
        set({
          rfNodes: rfNodes.map((n) =>
            n.id === p.id
              ? { ...n, position: { x: p.x, y: p.y }, data: { ...n.data, x: p.x, y: p.y } }
              : n,
          ),
        });
        break;
      }

      case "shape_moved": {
        const p = msg.payload as ShapeMovedPayload;
        const { shapes, rfShapes } = get();
        const current = shapes.find((s) => s.id === p.id);
        // A shape this canvas has never seen — a different forge, or deleted
        // locally. Nothing to move.
        if (!current) break;
        const updated: CanvasShape = {
          ...current,
          x: p.x,
          y: p.y,
          ...(p.x2 !== undefined ? { x2: p.x2 } : {}),
          ...(p.y2 !== undefined ? { y2: p.y2 } : {}),
          ...(p.width !== undefined ? { width: p.width } : {}),
          ...(p.height !== undefined ? { height: p.height } : {}),
        };
        // Rebuilt through toRFShape rather than patched: line-like shapes derive
        // their RF position and size from both endpoints, so a hand-patched
        // twin would render the old box around the new geometry.
        set({
          shapes: shapes.map((s) => (s.id === p.id ? updated : s)),
          rfShapes: rfShapes.map((n) => (n.id === `${SHAPE_PREFIX}${p.id}` ? toRFShape(updated) : n)),
        });
        break;
      }

      case "node_added": {
        const p = msg.payload as WireNode;
        if (rfNodes.some((n) => n.id === p.id)) break;
        set({
          rfNodes: [...rfNodes, toRFNode(mapNode(p))],
          pendingMeasureIds: [...get().pendingMeasureIds, p.id],
          // The row exists server-side, so this client may now delete it. Without
          // this the user could remove an MCP-created node and the removal would
          // fall outside the save's delete scope, leaving it on the canvas.
          knownIds: new Set(get().knownIds).add(p.id),
        });
        break;
      }

      case "node_deleted": {
        const p = msg.payload as DeletedPayload;
        set({ rfNodes: rfNodes.filter((n) => n.id !== p.id) });
        break;
      }

      case "edge_added": {
        const p = msg.payload as WireEdge;
        if (rfEdges.some((e) => e.id === p.id)) break;
        const shapeIds = new Set(get().shapes.map((s) => s.id));
        set({
          rfEdges: [...rfEdges, toRFEdge(mapEdge(p), shapeIds)],
          knownIds: new Set(get().knownIds).add(p.id),
        });
        break;
      }

      case "edge_deleted": {
        const p = msg.payload as DeletedPayload;
        set({ rfEdges: rfEdges.filter((e) => e.id !== p.id) });
        break;
      }

      case "shape_added": {
        const p = msg.payload as WireShape;
        const { shapes, rfShapes } = get();
        if (shapes.some((s) => s.id === p.id)) break;
        const shape = mapShape(p);
        const rfShape = toRFShape(shape);
        set({
          shapes: [...shapes, shape],
          rfShapes: [...rfShapes, rfShape],
          // Edges can terminate on a shape, so shapes need measuring too. The
          // RF id carries SHAPE_PREFIX — queue that, not the raw shape id.
          pendingMeasureIds: [...get().pendingMeasureIds, rfShape.id],
          // knownIds is a server-side scope, so it takes the raw id, never the
          // prefixed RF one.
          knownIds: new Set(get().knownIds).add(p.id),
        });
        break;
      }

      case "page_added": {
        const p = msg.payload as NodePage;
        const parent = rfNodes.find((n) => n.id === p.node_id);
        // A page for a node this canvas doesn't have (different forge, or the
        // node was deleted locally) has nowhere to attach.
        if (!parent) break;

        // If the drawer already loaded this node's pages, the loaded list is
        // authoritative for the badge — append there (deduped). The count bump
        // below covers nodes whose pages were never fetched.
        const pageStore = useNodePageStore.getState();
        const loaded = pageStore.pagesByNode[p.node_id];
        if (loaded) {
          if (loaded.some((pg) => pg.id === p.id)) break;
          pageStore.setPages(p.node_id, [...loaded, p]);
        }

        set({
          rfNodes: rfNodes.map((n) =>
            n.id === p.node_id
              ? { ...n, data: { ...n.data, pageCount: (n.data.pageCount ?? 0) + 1 } }
              : n,
          ),
        });
        break;
      }

      case "canvas_sync": {
        const p = msg.payload as ForgeCanvasPayload;
        get().applyServerCanvas(p);
        break;
      }

      default:
        break;
    }

    // Adopt the revision the frame states, but only having applied its patch —
    // that is what makes this canvas current as of that revision. Guarded three
    // ways, each for a different way of being wrong:
    //
    //  - canvas_revision is excluded: it carries no entities, so hearing it does
    //    not make this client current. AppShell refetches on it instead.
    //  - a frame with no revision (a peer-relayed drag) leaves the base alone.
    //  - never move backwards, so a frame delivered late cannot undo a newer
    //    base and send the next save into a conflict it already resolved.
    if (msg.type !== "canvas_revision" && msg.revision && msg.revision > get().serverRevision) {
      set({ serverRevision: msg.revision });
    }
  },

  // Called by MeasureHealer once React Flow has re-measured these ids. Ids whose
  // element was not in the DOM yet are not passed back, so they stay queued for
  // the next pass.
  clearPendingMeasureIds: (ids) => {
    if (ids.length === 0) return;
    const drop = new Set(ids);
    const next = get().pendingMeasureIds.filter((id) => !drop.has(id));
    if (next.length === get().pendingMeasureIds.length) return;
    set({ pendingMeasureIds: next });
  },

  markSaved: (revision, sentIds, serverRevision) => {
    const { rfNodes, rfEdges, shapes } = get();
    set({
      syncedRevision: revision,
      // The base for the next save. Taking the server's own answer rather than
      // incrementing locally keeps the two in step even when a save is retried
      // or a response arrives out of order.
      serverRevision,
      consecutiveSaveFailures: 0,
      saveError: null,
      // The server just acted on this scope: what we sent is alive, and anything
      // known-but-unsent was pruned. Rebuilding from the payload rather than
      // appending to the old set keeps deleted ids from accumulating forever.
      // The union with current state covers rows that landed while the request
      // was in flight — they exist server-side and are not in sentIds.
      knownIds: new Set([
        ...sentIds,
        ...rfNodes.map((n) => n.id),
        ...rfEdges.map((e) => e.id),
        ...shapes.map((s) => s.id),
      ]),
    });
  },

  recordSaveFailure: (reason) => set({
    consecutiveSaveFailures: get().consecutiveSaveFailures + 1,
    saveError: reason,
  }),

  // ── Reveal actions ────────────────────────────────────────────────────────

  enterRevealMode: () => {
    const { rfShapes, rfNodes, revealSequence } = get();
    set({
      isRevealMode: true,
      revealPointer: 0,
      isRevealEditMode: false,
      revealSequence: syncRevealSequence(revealSequence, rfShapes, rfNodes),
    });
  },

  exitRevealMode: () => set({ isRevealMode: false, revealPointer: 0 }),

  advanceReveal: () => {
    const { revealPointer, rfNodes, rfShapes } = get();
    const maxStep = Math.max(
      0,
      ...rfNodes.map(n => (n.data as KFNodeData).revealStep),
      ...rfShapes.map(n => (n.data as unknown as CanvasShape).revealStep),
    );
    set({ revealPointer: Math.min(revealPointer + 1, maxStep) });
  },

  retreatReveal: () => {
    const { revealPointer } = get();
    set({ revealPointer: Math.max(revealPointer - 1, 0) });
  },

  enterRevealEditMode: () => {
    const { rfShapes, rfNodes, revealSequence } = get();
    set({
      isRevealEditMode: true,
      isRevealMode: false,
      revealPointer: 0,
      revealSequence: syncRevealSequence(revealSequence, rfShapes, rfNodes),
    });
  },

  exitRevealEditMode: () => set({ isRevealEditMode: false, revealHighlightId: null }),

  setRevealHighlightId: (id) => set({ revealHighlightId: id }),

  removeFromRevealSequence: (rfId) => {
    const { revealSequence } = get();
    if (rfId.startsWith(SHAPE_PREFIX)) {
      get().updateShape(rfId.slice(SHAPE_PREFIX.length), { revealStep: 0 });
    } else {
      get().updateNode(rfId, { revealStep: 0 });
    }
    set({ revealSequence: revealSequence.filter(id => id !== rfId) });
  },

  moveToRevealStep: (rfId, step) => {
    const { revealSequence } = get();
    if (rfId.startsWith(SHAPE_PREFIX)) {
      get().updateShape(rfId.slice(SHAPE_PREFIX.length), { revealStep: step });
    } else {
      get().updateNode(rfId, { revealStep: step });
    }
    if (step === 0) {
      set({ revealSequence: revealSequence.filter(id => id !== rfId) });
      return;
    }
    // Place after the last item already in the target step, or append
    const updatedNodes  = get().rfNodes;
    const updatedShapes = get().rfShapes;
    const allOrdered    = [...updatedShapes, ...updatedNodes].map(n => n.id);
    const stepIds       = allOrdered.filter(id => {
      const node = updatedNodes.find(n => n.id === id) ?? updatedShapes.find(n => n.id === id);
      return node && (node.data as KFNodeData).revealStep === step && id !== rfId;
    });
    const withoutMoved  = revealSequence.filter(id => id !== rfId);
    if (stepIds.length === 0) {
      set({ revealSequence: [...withoutMoved, rfId] });
      return;
    }
    const lastStepId  = stepIds[stepIds.length - 1];
    const insertAfter = withoutMoved.lastIndexOf(lastStepId);
    const insertIdx   = insertAfter === -1 ? withoutMoved.length : insertAfter + 1;
    set({ revealSequence: [...withoutMoved.slice(0, insertIdx), rfId, ...withoutMoved.slice(insertIdx)] });
  },

  reorderWithinStep: (rfId, beforeRfId, _step) => {
    const { revealSequence } = get();
    const without = revealSequence.filter(id => id !== rfId);
    if (beforeRfId === null) {
      set({ revealSequence: [...without, rfId] });
      return;
    }
    const targetIdx = without.indexOf(beforeRfId);
    if (targetIdx === -1) {
      set({ revealSequence: [...without, rfId] });
    } else {
      set({ revealSequence: [...without.slice(0, targetIdx), rfId, ...without.slice(targetIdx)] });
    }
  },
}));

// ── localStorage persistence ──────────────────────────────────────────────────
// Keys are per forge (see canvasPersistence.ts). Previously a single key held
// whichever forge was last touched, so tabs on different forges overwrote each
// other and only the last forge could ever be restored.─

/** Store slice restored from a persisted canvas. Shared by reload-rehydration
 *  and by loadCanvas when local storage holds edits the server never received. */
function persistedState(saved: PersistedCanvas) {
  return {
    rfNodes:        saved.rfNodes        ?? [],
    rfEdges:        saved.rfEdges        ?? [],
    shapes:         saved.shapes         ?? [],
    rfShapes:       (saved.shapes        ?? []).map(toRFShape),
    forgeId:        saved.forgeId,
    revealSequence: saved.revealSequence ?? [],
    // Carry the counters across, or the unsaved edits we just restored would
    // look saved and get overwritten by the server's older copy.
    revision:       saved.revision       ?? 0,
    syncedRevision: saved.syncedRevision ?? 0,
    // A stored entry from before this field existed restores as 0, which is a
    // real revision and almost certainly not the current one — so the first
    // save conflicts, reconciles, and carries on. One wasted round trip on one
    // load, rather than a save applied over someone else's write.
    serverRevision: saved.serverRevision ?? 0,
    // Derived rather than stored: a Set does not survive JSON, and these ids came
    // from a server load in the first place. A restored tab that claimed nothing
    // could not delete anything until its next reload.
    knownIds: new Set([
      ...(saved.rfNodes ?? []).map((n) => n.id),
      ...(saved.rfEdges ?? []).map((e) => e.id),
      ...(saved.shapes  ?? []).map((s) => s.id),
    ]),
  };
}

/** Restore the forge that was open when the tab was last closed. */
export function rehydrateFromStorage(): void {
  migrateLegacyCanvas();
  const forgeId = lastActiveForgeId();
  if (!forgeId) return;
  const saved = readCanvas(forgeId);
  if (!saved) return;
  if (saved.rfNodes?.length || saved.rfEdges?.length || saved.shapes?.length) {
    useCanvasStore.setState(persistedState(saved));
  }
}

// ── Throttled writes ──────────────────────────────────────────────────────────
// The subscriber fires on every store update, so an unthrottled write ran a
// full JSON.stringify of the canvas plus a synchronous setItem hundreds of
// times during a single node drag, on the main thread. Coalesce to one write
// per window, always carrying the latest state.

const PERSIST_THROTTLE_MS = 300;
let _persistTimer: ReturnType<typeof setTimeout> | null = null;
let _persistPending: PersistedCanvas | null = null;

/** Write any pending canvas immediately. Call before the tab goes away. */
export function flushCanvasPersistence(): void {
  if (_persistTimer !== null) {
    clearTimeout(_persistTimer);
    _persistTimer = null;
  }
  if (_persistPending) {
    writeCanvas(_persistPending);
    _persistPending = null;
  }
}

export function startCanvasPersistence(): () => void {
  const unsubscribe = useCanvasStore.subscribe((state) => {
    // The forge changed with a write still queued: flush it under its own key
    // before the pending slot is reused, or the last edits on the forge we are
    // leaving never reach storage.
    if (_persistPending && _persistPending.forgeId !== state.forgeId) {
      flushCanvasPersistence();
    }
    if (!state.forgeId) return; // nothing to key the entry on
    _persistPending = {
      forgeId:        state.forgeId,
      rfNodes:        state.rfNodes,
      rfEdges:        state.rfEdges,
      shapes:         state.shapes,
      revealSequence: state.revealSequence,
      revision:       state.revision,
      syncedRevision: state.syncedRevision,
      serverRevision: state.serverRevision,
    };
    if (_persistTimer === null) {
      _persistTimer = setTimeout(flushCanvasPersistence, PERSIST_THROTTLE_MS);
    }
  });

  // pagehide rather than beforeunload: it also fires when a mobile browser
  // backgrounds the tab, which beforeunload does not.
  const onHide = () => flushCanvasPersistence();
  window.addEventListener("pagehide", onHide);

  // Another tab edited the forge we are showing. The `storage` event fires only
  // in *other* tabs, never the one that wrote, so there is no self-trigger.
  const onStorage = (e: StorageEvent) => {
    const state = useCanvasStore.getState();
    if (!state.forgeId || e.key !== canvasKey(state.forgeId) || !e.newValue) return;
    // Never adopt over unsaved work of our own — that edit exists nowhere else.
    if (state.revision !== state.syncedRevision) return;
    const incoming = readCanvas(state.forgeId);
    // Strictly newer only, or two tabs would adopt each other's writes forever.
    if (!incoming || incoming.revision <= state.revision) return;
    useCanvasStore.setState(persistedState(incoming));
  };
  window.addEventListener("storage", onStorage);

  return () => {
    unsubscribe();
    window.removeEventListener("pagehide", onHide);
    window.removeEventListener("storage", onStorage);
  };
}

if (typeof window !== "undefined") {
  rehydrateFromStorage();
  startCanvasPersistence();
}
